import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, todayUkIso } from "../lib/datetime";
import { Link } from "react-router-dom";
import { CalendarDays, Plus, Save, RefreshCw, Trash2, CheckCircle2, AlertTriangle, Wand2, X, BarChart3, Lock } from "lucide-react";

export default function DailyLog() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // editing existing or {date,prefill} for new
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/daily-logs");
      setList(data || []);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = async () => {
    const date = todayUkIso();
    try {
      const { data: pre } = await api.get(`/daily-logs/prefill`, { params: { date } });
      setEditing({ mode: "new", date, structures: pre.structures, gang: pre.gang, source_visit_ids: pre.source_visit_ids, notes: "" });
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startEdit = (l) => setEditing({ mode: "edit", ...l });

  const remove = async (id) => {
    if (!window.confirm("Delete this daily log?")) return;
    try { await api.delete(`/daily-logs/${id}`); setSuccess("Log deleted"); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="daily-log-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>SUPERVISOR · DAILY SITE LOG · MULTI-STRUCTURE</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
          <CalendarDays size={26} color="#0f172a" /> Daily logs
        </h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>One log per supervisor per day, covering every structure worked on. Per-structure % completion feeds invoicing. Auto-prefills from the day&apos;s visits — you edit and confirm.</div>
      </header>

      {error ? <Banner type="error" msg={error} onClose={() => setError("")} testid="dlog-error" /> : null}
      {success ? <Banner type="success" msg={success} onClose={() => setSuccess("")} testid="dlog-success" /> : null}

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button data-testid="dlog-new" className="btn-primary" onClick={startNew}><Plus size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />New daily log (today, prefilled)</button>
        <Link to="/weekly-report" data-testid="dlog-go-weekly" className="btn-ghost" style={{ textDecoration: "none", padding: "10px 14px" }}><BarChart3 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Weekly report</Link>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="dlog-table">
          <thead><tr><th>Date</th><th>Supervisor</th><th>Structures</th><th>Gang</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No daily logs yet. Click &quot;New daily log&quot; to start.</td></tr> :
              list.map((l) => (
                <tr key={l.id} data-testid={`dlog-row-${l.id}`}>
                  <td data-label="Date" className="mono" style={{ fontSize: 12 }}>{l.date} <span style={{ color: "#64748b" }}>({fmtDate(l.date)})</span></td>
                  <td data-label="Supervisor" style={{ fontWeight: 600 }}>{l.supervisor_name || "—"}</td>
                  <td data-label="Structures">
                    {(l.structures || []).length === 0 ? <span style={{ color: "#94a3b8" }}>—</span> :
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {(l.structures || []).map((s) => (
                          <div key={s.structure_id} style={{ fontSize: 12 }}>
                            <span style={{ background: percentBg(s.percent_complete), color: percentFg(s.percent_complete), padding: "1px 6px", borderRadius: 3, fontFamily: "IBM Plex Mono, monospace", fontWeight: 700, fontSize: 10 }}>{s.percent_complete}%</span>
                            <span style={{ marginLeft: 6 }}>{s.structure_description || s.structure_id.slice(0, 8)}</span>
                          </div>
                        ))}
                      </div>}
                  </td>
                  <td data-label="Gang" className="mono">{(l.gang || []).length}</td>
                  <td data-label="Status"><StatusBadge s={l.status} /></td>
                  <td data-label="Actions" style={{ textAlign: "right" }}>
                    <button data-testid={`dlog-edit-${l.id}`} className="btn-ghost" onClick={() => startEdit(l)}>Edit</button>
                    {["admin", "contracts_manager"].includes(user?.role) ? <button data-testid={`dlog-delete-${l.id}`} className="btn-ghost" onClick={() => remove(l.id)} style={{ color: "#b91c1c" }}><Trash2 size={12} /></button> : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editing ? <DailyLogEditor data={editing} onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); setSuccess(msg); load(); }} onError={(m) => setError(m)} /> : null}
    </div>
  );
}

function percentBg(p) { return p >= 100 ? "#059669" : p >= 50 ? "#0ea5e9" : p > 0 ? "#f59e0b" : "#94a3b8"; }
function percentFg() { return "#fff"; }

function StatusBadge({ s }) {
  const c = s === "confirmed" ? { bg: "#dcfce7", fg: "#065f46", label: "CONFIRMED" } : { bg: "#fef3c7", fg: "#92400e", label: "DRAFT" };
  return <span className="mono" style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{c.label}</span>;
}

function DailyLogEditor({ data, onClose, onSaved, onError }) {
  const { user } = useAuth();
  const [date, setDate] = useState(data.date || todayUkIso());
  const [structures, setStructures] = useState(data.structures || []);
  const [gang, setGang] = useState(data.gang || []);
  const [notes, setNotes] = useState(data.notes || "");
  const [status, setStatus] = useState(data.status || "draft");
  const [allStructures, setAllStructures] = useState([]);
  const [allOps, setAllOps] = useState([]);
  const [busy, setBusy] = useState(false);
  const isEdit = data.mode === "edit";
  const isConfirmedAndSup = isEdit && status === "confirmed" && user?.role === "supervisor";

  useEffect(() => {
    api.get("/structures").then((r) => setAllStructures(r.data || []));
    api.get("/operatives").then((r) => setAllOps(r.data || [])).catch(() => setAllOps([]));
  }, []);

  const reprefill = async () => {
    try {
      const { data: pre } = await api.get(`/daily-logs/prefill`, { params: { date } });
      setStructures(pre.structures || []);
      setGang(pre.gang || []);
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addStructure = () => setStructures([...structures, { structure_id: "", percent_complete: 0, work_summary: "" }]);
  const updStructure = (i, k, v) => setStructures(structures.map((s, idx) => idx === i ? { ...s, [k]: k === "percent_complete" ? Number(v) : v } : s));
  const removeStructure = (i) => setStructures(structures.filter((_, idx) => idx !== i));
  const addGang = () => setGang([...gang, { operative_id: "", hours: null }]);
  const updGang = (i, k, v) => setGang(gang.map((g, idx) => idx === i ? { ...g, [k]: k === "hours" ? (v ? Number(v) : null) : v } : g));
  const removeGang = (i) => setGang(gang.filter((_, idx) => idx !== i));

  const save = async (newStatus) => {
    setBusy(true);
    try {
      const payload = { structures: structures.filter((s) => s.structure_id), gang: gang.filter((g) => g.operative_id), notes, status: newStatus || status };
      if (isEdit) {
        await api.patch(`/daily-logs/${data.id}`, payload);
        onSaved(newStatus === "confirmed" ? "Log confirmed" : "Log saved");
      } else {
        await api.post("/daily-logs", { date, ...payload, source_visit_ids: data.source_visit_ids || [] });
        onSaved(newStatus === "confirmed" ? "Log created & confirmed" : "Log created");
      }
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="dlog-editor" className="card" style={{ background: "white", maxWidth: 820, width: "100%", padding: 24, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>{isEdit ? "Edit daily log" : "New daily log"}{isConfirmedAndSup ? <span title="Confirmed — locked for supervisors"><Lock size={16} style={{ marginLeft: 8, verticalAlign: -2 }} /></span> : null}</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <Field label="Date *">
            <input data-testid="dlog-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} disabled={isEdit} />
          </Field>
          <Field label="Re-prefill from today's visits">
            <button type="button" data-testid="dlog-reprefill" className="btn-ghost" onClick={reprefill} disabled={isConfirmedAndSup}><Wand2 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Re-prefill</button>
          </Field>
        </div>

        <h3 style={{ margin: "8px 0 6px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Structures & % completion</h3>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 4, padding: 8, marginBottom: 12 }}>
          {structures.length === 0 ? <div style={{ color: "#94a3b8", padding: 8, fontSize: 12 }}>No structures yet.</div> :
            structures.map((s, i) => (
              <div key={i} data-testid={`dlog-struct-row-${i}`} style={{ display: "grid", gridTemplateColumns: "2fr 70px 3fr 28px", gap: 6, padding: 6, borderBottom: i < structures.length - 1 ? "1px solid #f1f5f9" : "none", alignItems: "center" }}>
                <select data-testid={`dlog-struct-pick-${i}`} value={s.structure_id} onChange={(e) => updStructure(i, "structure_id", e.target.value)} disabled={isConfirmedAndSup}>
                  <option value="">— pick structure —</option>
                  {allStructures.map((x) => <option key={x.id} value={x.id}>{x.description}</option>)}
                </select>
                <input data-testid={`dlog-struct-pct-${i}`} type="number" min={0} max={100} value={s.percent_complete} onChange={(e) => updStructure(i, "percent_complete", e.target.value)} disabled={isConfirmedAndSup} title="% complete" />
                <input data-testid={`dlog-struct-work-${i}`} value={s.work_summary} placeholder="What was done" onChange={(e) => updStructure(i, "work_summary", e.target.value)} disabled={isConfirmedAndSup} />
                <button type="button" data-testid={`dlog-struct-rm-${i}`} className="btn-ghost" onClick={() => removeStructure(i)} disabled={isConfirmedAndSup} style={{ color: "#b91c1c" }}><Trash2 size={12} /></button>
              </div>
            ))}
          <button type="button" data-testid="dlog-add-struct" className="btn-ghost" onClick={addStructure} disabled={isConfirmedAndSup} style={{ marginTop: 6 }}><Plus size={12} style={{ marginRight: 4 }} />Add structure</button>
        </div>

        <h3 style={{ margin: "8px 0 6px", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Gang on site</h3>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 4, padding: 8, marginBottom: 12 }}>
          {gang.length === 0 ? <div style={{ color: "#94a3b8", padding: 8, fontSize: 12 }}>No gang members yet.</div> :
            gang.map((g, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 80px 28px", gap: 6, padding: 6, borderBottom: i < gang.length - 1 ? "1px solid #f1f5f9" : "none", alignItems: "center" }}>
                <select data-testid={`dlog-gang-pick-${i}`} value={g.operative_id} onChange={(e) => updGang(i, "operative_id", e.target.value)} disabled={isConfirmedAndSup}>
                  <option value="">— pick operative —</option>
                  {allOps.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                </select>
                <input data-testid={`dlog-gang-hours-${i}`} type="number" min={0} max={24} step="0.5" value={g.hours ?? ""} placeholder="hrs" onChange={(e) => updGang(i, "hours", e.target.value)} disabled={isConfirmedAndSup} />
                <button type="button" className="btn-ghost" onClick={() => removeGang(i)} disabled={isConfirmedAndSup} style={{ color: "#b91c1c" }}><Trash2 size={12} /></button>
              </div>
            ))}
          <button type="button" data-testid="dlog-add-gang" className="btn-ghost" onClick={addGang} disabled={isConfirmedAndSup} style={{ marginTop: 6 }}><Plus size={12} style={{ marginRight: 4 }} />Add gang member</button>
        </div>

        <Field label="Notes"><textarea data-testid="dlog-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={isConfirmedAndSup} style={{ width: "100%" }} /></Field>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          {isConfirmedAndSup ? null : (
            <>
              <button type="button" data-testid="dlog-save-draft" className="btn-ghost" onClick={() => save("draft")} disabled={busy}><Save size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Save draft</button>
              <button type="button" data-testid="dlog-confirm" className="btn-primary" onClick={() => save("confirmed")} disabled={busy}><CheckCircle2 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Confirm</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Banner({ type, msg, onClose, testid }) {
  const ok = type === "success";
  return (
    <div data-testid={testid} className="card" style={{ padding: 12, marginBottom: 12, background: ok ? "#ecfdf5" : "#fef2f2", color: ok ? "#065f46" : "#b91c1c", borderLeft: `4px solid ${ok ? "#059669" : "#dc2626"}`, display: "flex", gap: 8, alignItems: "center" }}>
      {ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />} {msg}
      {onClose ? <button className="btn-ghost" onClick={onClose} style={{ marginLeft: "auto" }}><X size={12} /></button> : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 4 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
